import { BitkitButton, IconArrowRight, rem } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Skeleton } from '@chakra-ui/react/skeleton';
import { Text } from '@chakra-ui/react/text';
import { useState } from 'react';

import Dot from '@/components/Dot/Dot';
import useLatestActivities from '@/hooks/api/useLatestActivities';
import { formatDateTime } from '@/utils/commonFormats';

const getActivityColor = (title: string) => {
  if (title.includes('failed')) {
    return 'color/red/bold';
  }

  if (title.includes('finished')) {
    return 'color/green/bold';
  }

  if (title.includes('limit reached')) {
    return 'color/yellow/bold';
  }

  return 'color/neutral/moderate';
};

interface ActivityListProps {
  limit: number;
  next?: string;
  autoLoad?: boolean;
  showMoreButton?: boolean;
}

const ActivityList = ({ limit, next, autoLoad = true, showMoreButton = false }: ActivityListProps) => {
  const [loaded, setLoaded] = useState(autoLoad);
  const { data: activityResponse, isLoading } = useLatestActivities(loaded, limit, next);

  if (!loaded) {
    return (
      <BitkitButton
        key="show-all-notifications"
        variant="tertiary"
        suffixIcon={IconArrowRight}
        size="sm"
        marginTop="8"
        marginLeft={rem(36)}
        onClick={() => setLoaded(true)}
      >
        Load more
      </BitkitButton>
    );
  }

  return (
    <>
      <Box display="flex" flexDirection="column">
        {isLoading &&
          [...Array(limit)]
            .map((_, index) => `loading-${index}`)
            .map((key) => (
              <Box key={key} overflow="hidden" paddingY="8" paddingX="24">
                <Skeleton height="24" width="80%" marginBottom="4" />
                <Skeleton height="16" width="70%" marginBottom="4" />
              </Box>
            ))}
        {activityResponse?.data.map((event) => {
          return (
            <Box
              key={event.slug}
              asChild
              display="flex"
              gap="16"
              paddingBlock="8"
              paddingInline="24"
              _active={{ backgroundColor: 'background/active' }}
              _hover={{ backgroundColor: 'background/hover' }}
            >
              <a href={event.targetPathString}>
                <Dot size="8" backgroundColor={getActivityColor(event.title)} marginBlock="8" flexShrink="0" />
                <Box>
                  <Text color="text/body" textStyle="body/lg/regular" overflowWrap="anywhere">
                    {event.description}
                  </Text>
                  <Text color="text/secondary" textStyle="body/md/regular">
                    <time dateTime={event.createdAt} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatDateTime(event.createdAt, { hideYear: true, hideSeconds: true })}
                    </time>
                  </Text>
                </Box>
              </a>
            </Box>
          );
        })}
      </Box>
      {showMoreButton && activityResponse?.paging.next && (
        <ActivityList
          limit={limit}
          next={activityResponse?.paging.next}
          showMoreButton={showMoreButton}
          autoLoad={false}
        />
      )}
    </>
  );
};

export default ActivityList;
